import { parseMillimeters } from '@/lib/utils/dimensions';

export type SupplierBleedLayout = {
  /** Bleed per side in mm. */
  bleedMm: number;
  /**
   * true: the supplier wants the file at final size + bleed on every side
   * (Print.com "aan te leveren formaat" = eindformaat + 2 x afloop; Probo idem).
   * The ordered size is then the trim size, never the page size.
   */
  outside: boolean;
};

function parseFormatMm(value: unknown): { width: number; height: number } | null {
  if (typeof value !== 'string') return null;
  const match = value.replace(/,/g, '.').match(/^\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*(mm|cm)?\s*$/i);
  if (!match) return null;
  const factor = /cm/i.test(match[3] || '') ? 10 : 1;
  return { width: Number(match[1]) * factor, height: Number(match[2]) * factor };
}

/**
 * Where the supplier's bleed lives, read from Product.specifications.
 * Print.com syncs `bleed` together with `endFormat` and `deliveryFormat`; for
 * 545 of the 607 products that have both, deliveryFormat = endFormat + 2 x bleed.
 * Products whose deliveryFormat equals the endFormat (mostly promotional items
 * with a print area) keep the old layout (bleed inside the page).
 */
export function supplierBleedLayout(specifications: unknown): SupplierBleedLayout | null {
  const specs = specifications && typeof specifications === 'object' && !Array.isArray(specifications)
    ? specifications as Record<string, any> : {};
  const bleed = parseMillimeters(specs.bleed ?? specs.fileRequirements?.bleed);
  if (bleed === null) return null;
  const end = parseFormatMm(specs.endFormat);
  const delivery = parseFormatMm(specs.deliveryFormat);
  if (end && delivery) {
    const perSideX = (delivery.width - end.width) / 2;
    const perSideY = (delivery.height - end.height) / 2;
    if (Math.abs(perSideX) < 0.3 && Math.abs(perSideY) < 0.3) return { bleedMm: bleed, outside: false };
    // The synced format strings are not reliable about units (v-bord: "100 x 70 mm"
    // for a 100 x 70 cm board), so they only decide inside/outside; the amount
    // stays the synced bleed value.
    if (perSideX > 0 && perSideY > 0) return { bleedMm: bleed, outside: bleed > 0 };
  }
  return { bleedMm: bleed, outside: bleed > 0 };
}

/** Editor page (= PDF media box) for a final size: final + bleed on every side when the supplier wants it outside. */
export function editorPageSizeMm(
  final: { width: number; height: number },
  bleed: { top: number; right: number; bottom: number; left: number },
  bleedOutside: boolean,
) {
  if (!bleedOutside) return { width: final.width, height: final.height };
  return { width: final.width + bleed.left + bleed.right, height: final.height + bleed.top + bleed.bottom };
}
